export * from "ce/selectors/environmentSelectors";
import { DEFAULT_ENV_ID } from "ee/api/ApiUtils";
import { FEATURE_FLAG } from "ee/entities/FeatureFlag";
import type { DefaultRootState } from "react-redux";
import type { EnvironmentType } from "ee/configs/types";
import { getFilteredEnvListWithPermissions } from "ee/utils/Environments";
import { hasManageWorkspaceEnvironmentPermission } from "ee/utils/permissionHelpers";
import { selectFeatureFlagCheck } from "ee/selectors/featureFlagsSelectors";
import { getHasManageWorkspaceDatasourcePermission } from "ee/utils/BusinessFeatures/permissionPageHelpers";
import { getCurrentAppWorkspace } from "ee/selectors/selectedWorkspaceSelectors";
import { onCanvas } from "components/editorComponents/Debugger/helpers";
import { matchDatasourcePath } from "constants/routes";
import { isDatasourceInViewMode } from "selectors/ui";

export const getEnvironmentByName = (state: DefaultRootState, name: string) => {
  const environments = state.environments.data;
  const environment = environments.find((env) => env.name === name);

  return environment;
};

export const getEnvironmentById = (state: DefaultRootState, id: string) => {
  const environments = state.environments.data;
  const environment = environments.find((env) => env.id === id);

  return environment;
};

export const getEnvironmentIdByName = (
  state: DefaultRootState,
  name: string,
) => {
  if (name === DEFAULT_ENV_ID) return name;

  const environments = state.environments.data;
  const environment = environments.find((env) => env.name === name);

  return environment?.id || DEFAULT_ENV_ID;
};

export const getDefaultEnvironmentId = (state: DefaultRootState) => {
  const environments = state.environments.data;

  if (!environments || environments.length === 0) return DEFAULT_ENV_ID;

  const environment = environments.find((env) => env.isDefault === true);

  return environment?.id || environments[0].id;
};

export const getDefaultEnvironment = (state: DefaultRootState) => {
  const environments = getEnvironmentsWithPermission(state);
  const environment = environments.find((env) => env.isDefault === true);

  return environment;
};

export const getEnvironments = (
  state: DefaultRootState,
): Array<EnvironmentType> => state.environments.data;

export const getEnvironmentsWithPermission = (
  state: DefaultRootState,
): Array<EnvironmentType> =>
  getFilteredEnvListWithPermissions(getEnvironments(state));

export const areEnvironmentsFetched = (
  state: DefaultRootState,
  workspaceId: string,
) =>
  state.environments.data.length > 0 &&
  state.environments.data[0].workspaceId === workspaceId;

export const getCurrentEnvironmentId = (state: DefaultRootState) =>
  state.environments.currentEnvironmentDetails.id || "unused_env";

export const getCurrentEnvironmentName = (state: DefaultRootState) =>
  state.environments.currentEnvironmentDetails.name || "";

export const getCurrentEditingEnvironmentId = (state: DefaultRootState) =>
  state.environments.currentEnvironmentDetails.editingId || "unused_env";

export const getCurrentEnvironmentDetails = (state: DefaultRootState) =>
  state.environments.currentEnvironmentDetails;

export const isEnvironmentFetching = (state: DefaultRootState) =>
  state.environments.isLoading;

export const isEnvironmentUpdating = (state: DefaultRootState) =>
  state.environments.isUpdating;

export const allowManageEnvironmentAccessForUser = (
  state: DefaultRootState,
) => {
  const isFlagEnabled = selectFeatureFlagCheck(
    state,
    FEATURE_FLAG.license_custom_environments_enabled,
  );

  return isFlagEnabled;
};

export const allowManageEnvironmentAccessForWorkspace = (
  state: DefaultRootState,
  workspacePermissions: string[],
) => {
  const checkUserAccess = allowManageEnvironmentAccessForUser(state);
  const hasWorkspaceManageEnvPermission =
    hasManageWorkspaceEnvironmentPermission(workspacePermissions);

  return checkUserAccess && hasWorkspaceManageEnvPermission;
};

// Conditions to show the env walkthrough
// 1. Walkthrough is not shown before
// 2. The feature flag for release datasource environments is enabled
// 3. The feature flag for the env walkthrough is enabled
// 4. User should have edit datasource permissions
// 5. There should be no datasources with staging environment configured
// 6. For step 1, the user should be on the canvas page and for step 2, the user should be on the datasource page with edit mode enabled
export const renderEnvWalkthrough = (state: DefaultRootState, step = 1) => {
  // Check 1 is done in the component itself (since it is an async function call)

  // Check 2
  const isFlagEnabled = selectFeatureFlagCheck(
    state,
    FEATURE_FLAG.release_datasource_environments_enabled,
  );

  if (!isFlagEnabled) return false;

  // Check 3
  const isEnvWalkthroughEnabled = selectFeatureFlagCheck(
    state,
    FEATURE_FLAG.ab_env_walkthrough_enabled,
  );

  if (!isEnvWalkthroughEnabled) return false;

  // Check 4
  const isGACEnabledFlag = selectFeatureFlagCheck(
    state,
    FEATURE_FLAG.license_gac_enabled,
  );

  const userWorkspacePermissions =
    getCurrentAppWorkspace(state).userPermissions ?? [];

  const canManageDatasource = getHasManageWorkspaceDatasourcePermission(
    isGACEnabledFlag,
    userWorkspacePermissions,
  );

  if (!canManageDatasource) return false;

  // Check 5
  const environments = getEnvironments(state);
  const stagingEnvDetails = environments.find((env) => env.name === "Staging");

  if (
    !stagingEnvDetails ||
    !stagingEnvDetails.datasourceMeta ||
    !stagingEnvDetails.datasourceMeta.hasOwnProperty("configuredDatasources")
  )
    return false;

  if (stagingEnvDetails.datasourceMeta?.configuredDatasources > 0) return false;

  // Check 6
  if (step === 1 && onCanvas()) return true;

  const isDatasourceViewMode = isDatasourceInViewMode(state);

  if (
    step === 2 &&
    !!matchDatasourcePath(window.location.pathname) &&
    !isDatasourceViewMode
  )
    return true;

  return false;
};
